import { useEffect } from 'react';
import type { IOsasReferralData } from '@common/validation/customControls/osas/osasReferralSchemas/osasReferralSchema';
import type { ICheckBoxGroupWithTextFieldProps } from '@frontend/components/fields/CheckBoxGroup';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ComorbiditiesChoice } from '@common/enums';
import { RadioGroupWithAdditionalCheckboxes } from '@frontend/components/fields/RadioGroupWithAdditionalCheckboxes';
import { baseComorbidityValues } from '../osasReferralData';

interface IComorbiditiesFormGroupProps {
    data: IOsasReferralData;
    errors: ValidationErrors;
    isRequired: boolean;
    onChange: (data: Partial<IOsasReferralData>, validateKeys?: string | string[] | undefined) => void;
}

export function ComorbiditiesFormGroup(props: IComorbiditiesFormGroupProps) {
    const { data, errors, isRequired, onChange } = props;
    const comorbidityOptions: ICheckBoxGroupWithTextFieldProps['checkboxOptions'] = [
        {
            checked: data.comorbidities.highBloodPressure,
            label: 'Hoge bloeddruk',
            onChange: (value: boolean) => handleChangeComorbiditeiten('highBloodPressure', value),
        },
        {
            checked: data.comorbidities.cardialProblems,
            label: 'Cardiale problematiek',
            onChange: (value: boolean) => handleChangeComorbiditeiten('cardialProblems', value),
        },
        {
            checked: data.comorbidities.diabetesMellitus,
            label: 'Diabetes mellitus',
            onChange: (value: boolean) => handleChangeComorbiditeiten('diabetesMellitus', value),
        },
        {
            checked: data.comorbidities.burnoutOrDepressed,
            label: 'Burn-out of depressieve klachten',
            onChange: (value: boolean) => handleChangeComorbiditeiten('burnoutOrDepressed', value),
        },
        {
            checked: data.comorbidities.COPD,
            label: 'COPD',
            onChange: (value: boolean) => handleChangeComorbiditeiten('COPD', value),
        },
        {
            label: 'Anders',
            onChange: (value: string | null) => handleChangeComorbiditeiten('other', value),
            value: data.comorbidities.other,
        },
    ];

    useEffect(() => {
        if (data.comorbidityChoice === ComorbiditiesChoice.nee) {
            onChange({
                ...data,
                comorbidities: baseComorbidityValues,
                comorbidityChoice: ComorbiditiesChoice.nee,
            });
        }
    }, [data.comorbidityChoice]);

    return (
        <RadioGroupWithAdditionalCheckboxes
            checkboxOptions={comorbidityOptions}
            error={errors.get('comorbidities') || errors.get('comorbidities.other')}
            fieldKey="comorbidityChoice"
            hasErrors={errors.has('comorbidities') || errors.has('comorbidities.other')}
            isRequired={isRequired}
            label="Is er sprake van relevante comorbiditeiten?"
            noLabel="Nee, er zijn geen relevante comorbiditeiten aanwezig"
            noValue={ComorbiditiesChoice.nee}
            onChange={handleChangeComorbiditeiten}
            value={data.comorbidityChoice}
            yesLabel="Ja, namelijk..."
            yesValue={ComorbiditiesChoice.ja}
        />
    );

    function handleChangeComorbiditeiten<T extends keyof IOsasReferralData['comorbidities']>(
        key: T,
        value: IOsasReferralData['comorbidities'][T]
    ) {
        onChange({ comorbidities: { ...data.comorbidities, [key]: value } }, ['comorbidities', `comorbidities.${key}`]);
    }
}
